// Copyright 2022 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import { ICredentials } from "@aws-amplify/core";
import React, { useState } from "react";
import { getAuthType } from "../../../configurations";
import { AuthType, BrowserType } from "../../../enums";
import OidcBrowserCreator from "../browserCreators/OidcBrowserCreator";
import Sigv4BrowserCreator from "../browserCreators/Sigv4BrowserCreator";
import log, { METRIC_NAME } from "../../../logging";
import { ERROR_STATE } from "../../../constants";
import ErrorAlert from "../../general/ErrorAlert";
import { useTranslation } from "react-i18next";
import { loadClient } from "../../../utils";
import { AWSError } from "aws-sdk";
import {
  getDeviceTypeMetricName,
  getDeviceType,
  isInternalTestTraffic,
  getDeviceBrowser,
  getDeviceModel,
  getDeviceOSName,
  getDeviceTouchInfo,
} from "../../../utils/userAgentUtils";

interface Props {
  onBrowserCreation: (browserType: BrowserType, endpoint: string) => void;
  sigv4Credentials?: ICredentials;
}

const BrowserFactory = (props: Props): JSX.Element => {
  const { onBrowserCreation, sigv4Credentials } = props;
  const authType = getAuthType();
  const { t } = useTranslation();

  const [errorState, setErrorState] = useState({
    errorState: ERROR_STATE.NONE,
    errorHeader: "lease.errorAlert.serverError.header",
    errorMessage: "lease.errorAlert.serverError.message",
  });

  const onError = (err: AWSError, metricName: METRIC_NAME) => {
    const { name, statusCode, message } = err;
    log.publishHttpStatusMetric(metricName, statusCode);
    switch (name) {
      case "ResourceNotReadyException":
        setErrorState({
          errorState: ERROR_STATE.INFO,
          errorHeader: "lease.errorAlert.pendingPortal.header",
          errorMessage: "lease.errorAlert.pendingPortal.message",
        });
        break;
      case "AccessDeniedException":
        if (
          message?.includes(
            "Cannot access resource because account is deleted."
          )
        ) {
          setErrorState({
            errorState: ERROR_STATE.INFO,
            errorHeader: "lease.errorAlert.suspendedPortal.header",
            errorMessage: "lease.errorAlert.suspendedPortal.message",
          });
        } else {
          setErrorState({
            errorState: ERROR_STATE.ERROR,
            errorHeader: errorState.errorHeader,
            errorMessage: errorState.errorMessage,
          });
        }
        break;
      case "InternalServerException":
        if (message?.includes("V750620258")) {
          setErrorState({
            errorState: ERROR_STATE.WARNING,
            errorHeader: "lease.errorAlert.userAccessLoggingBug.header",
            errorMessage: "lease.errorAlert.userAccessLoggingBug.message",
          });
        } else {
          setErrorState({
            errorState: ERROR_STATE.ERROR,
            errorHeader: errorState.errorHeader,
            errorMessage: errorState.errorMessage,
          });
        }
        break;
      default:
        setErrorState({
          errorState: ERROR_STATE.ERROR,
          errorHeader: errorState.errorHeader,
          errorMessage: errorState.errorMessage,
        });
    }
  };

  const onCreate = async (
    browserType: BrowserType,
    endpoint: string,
    metricName: METRIC_NAME
  ) => {
    log.publishHttpStatusMetric(metricName, 200);

    if (!isInternalTestTraffic()) {
      publishDeviceMetrics();
    }
    switch (browserType) {
      case BrowserType.APPSTREAM: {
        await loadClient(`${process.env.PUBLIC_URL}/appstream_embed.js`);
        onBrowserCreation(browserType, endpoint);
        break;
      }
      default: {
        setErrorState({
          errorState: ERROR_STATE.ERROR,
          errorHeader: errorState.errorHeader,
          errorMessage: errorState.errorMessage,
        });
        throw new Error("Invalid Browser created");
      }
    }
  };

  if (errorState.errorState === ERROR_STATE.NONE) {
    switch (authType) {
      case AuthType.STANDARD: {
        return (
          <Sigv4BrowserCreator
            onBrowserCreation={onCreate}
            onBrowserCreationError={onError}
            sigv4Credentials={sigv4Credentials!}
          />
        );
      }
      case AuthType.IAM_IDENTITY_CENTER: {
        return (
          <OidcBrowserCreator
            onBrowserCreation={onCreate}
            onBrowserCreationError={onError}
          />
        );
      }
      default: {
        log.error("Invalid authentication type");
        throw Error("Invalid authentication type");
      }
    }
  }

  return (
    <div className="awsui On-top">
      <ErrorAlert
        type={errorState.errorState}
        header={t(errorState.errorHeader)}
        message={t(errorState.errorMessage)}
        refreshButtonText={t("refresh.button.label")}
        onRefreshButtonClick={() => window.location.reload()}
        onDismissErrorAlert={() => {}}
      />
    </div>
  );
};

function publishDeviceMetrics(): void {
  log.publishCounterMetric(getDeviceTypeMetricName(getDeviceType()));
  log.publishMetricWithDimension(
    METRIC_NAME.DEVICE_BROWSER,
    getDeviceBrowser()
  );
  log.publishMetricWithDimension(METRIC_NAME.DEVICE_TYPE, getDeviceType());
  log.publishMetricWithDimension(METRIC_NAME.DEVICE_MODEL, getDeviceModel());
  log.publishMetricWithDimension(METRIC_NAME.DEVICE_OS, getDeviceOSName());
  log.publishMetricWithDimension(
    METRIC_NAME.DEVICE_TOUCHSCREEN,
    getDeviceTouchInfo()
  );
}

export default BrowserFactory;
